<template>
  <UModal v-model:open="isOpen" :ui="{ content: 'max-w-4xl' }">
    <template #content>
      <UCard>
        <template #header>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <UIcon name="i-heroicons-beaker" class="w-5 h-5 text-primary" />
              <h3 class="text-base font-bold">Abrechnung testen (Dry Run)</h3>
            </div>
            <UButton color="neutral" variant="ghost" icon="i-heroicons-x-mark" @click="isOpen = false" />
          </div>
        </template>

        <div class="space-y-4 text-sm">
          <p class="text-gray-500 dark:text-gray-400">
            Zeigt, welche Rechnungen die Abrechnung für den gewählten Monat erstellen würde. Es werden
            keine Rechnungen erstellt und nichts gespeichert.
          </p>

          <div class="flex flex-wrap items-end gap-3">
            <UFormField label="Abrechnungsmonat">
              <UInput v-model="period" type="month" />
            </UFormField>
            <UButton
              data-testid="billing-dry-run-start"
              :disabled="!period"
              :loading="loading"
              color="primary"
              icon="i-heroicons-play"
              label="Testlauf starten"
              @click="runDryRun"
            />
          </div>

          <UAlert v-if="errorMessage" color="error" variant="subtle" :description="errorMessage" />

          <template v-if="preview">
            <div data-testid="billing-dry-run-summary" class="flex flex-wrap gap-2">
              <UBadge
                v-for="item in summary"
                :key="item.status"
                :color="statusMeta[item.status].color"
                variant="subtle"
              >
                {{ item.count }} {{ statusMeta[item.status].label }}
              </UBadge>
            </div>

            <div class="max-h-[60vh] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
              <div
                v-for="plan in sortedCustomers"
                :key="plan.customerNo"
                data-testid="billing-dry-run-customer"
                class="py-3 space-y-1"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="font-medium">{{ plan.customerNo }} – {{ plan.customerName }}</span>
                  <UBadge :color="statusMeta[plan.status].color" variant="soft" size="sm">
                    {{ statusMeta[plan.status].label }}
                  </UBadge>
                </div>
                <p v-if="plan.error" class="text-xs text-error break-all">{{ plan.error }}</p>
                <table v-if="plan.positions?.length" class="w-full text-xs">
                  <tbody>
                    <tr v-for="(position, index) in plan.positions" :key="index">
                      <td class="pr-3 font-mono whitespace-nowrap">{{ position.product }}</td>
                      <td class="pr-3 text-right tabular-nums">{{ position.quantity }}</td>
                      <td class="text-gray-500 dark:text-gray-400">{{ position.description }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </UCard>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { billingApi, type BillingPlanStatus, type BillingPreview } from '@/api/billing'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:open'])

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit('update:open', val),
})

type BadgeColor = 'success' | 'error' | 'neutral' | 'info'

// Order also defines the sort order: problems first, then what would be invoiced.
const statusMeta: Record<BillingPlanStatus, { label: string; color: BadgeColor; order: number }> = {
  error: { label: 'Fehler', color: 'error', order: 0 },
  invoice: { label: 'Rechnung', color: 'success', order: 1 },
  already_invoiced: { label: 'Bereits abgerechnet', color: 'info', order: 2 },
  no_usage: { label: 'Keine Nutzung', color: 'neutral', order: 3 },
  excluded: { label: 'Ausgeschlossen', color: 'neutral', order: 4 },
}

const previousMonth = () => {
  const now = new Date()
  // From the 1st: on the 31st, "minus one month" would otherwise roll over.
  const date = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

const period = ref(previousMonth())
const loading = ref(false)
const errorMessage = ref('')
const preview = ref<BillingPreview | null>(null)

const sortedCustomers = computed(() =>
  [...(preview.value?.customers ?? [])].sort(
    (a, b) =>
      statusMeta[a.status].order - statusMeta[b.status].order ||
      a.customerNo.localeCompare(b.customerNo),
  ),
)

const summary = computed(() =>
  (Object.keys(statusMeta) as BillingPlanStatus[])
    .map((status) => ({
      status,
      count: preview.value?.customers.filter((c) => c.status === status).length ?? 0,
    }))
    .filter((item) => item.count > 0),
)

const runDryRun = async () => {
  const [year, month] = period.value.split('-').map(Number)
  loading.value = true
  errorMessage.value = ''
  preview.value = null
  try {
    const response = await billingApi.dryRun(year!, month!)
    preview.value = response.data
  } catch (error) {
    const apiError = (error as { response?: { data?: { error?: string } } }).response?.data?.error
    errorMessage.value = apiError || 'Der Testlauf der Abrechnung ist fehlgeschlagen.'
  } finally {
    loading.value = false
  }
}

defineExpose({ period })
</script>
